import { useState, useCallback } from 'react';
import axiosInstance from '../utils/axiosConfig';

const redirectToLoginIfNeeded = (url) => {
  if (typeof window === 'undefined') return;
  if (!url || url.includes('/auth/login')) return;
  window.location.href = '/login';
};

function useFetch() {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState(null);

  const execute = useCallback(async (url, method = 'GET', body = null, options = {}) => {
    const { retry = true, silent = false } = options;

    if (!silent) {
      setLoading(true);
      setError(null);
      setData(null);
      setStatus(null);
    }

    try {
      const config = {
        method,
        url,
        data: body ?? undefined,
      };

      const response = await axiosInstance.request(config);

      if (!silent) {
        setStatus(response.status);
        setData(response.data);
        setLoading(false);
      }

      return { status: response.status, ok: true, data: response.data };
    } catch (err) {
      const statusCode = err.response?.status;
      const isAuthError = statusCode === 401 || statusCode === 403;

      if (isAuthError && retry && !String(url || '').includes('/auth/login')) {
        try {
          await axiosInstance.post('/auth/refresh-token');
          return execute(url, method, body, { ...options, retry: false });
        } catch (refreshErr) {
          redirectToLoginIfNeeded(url);
          const errorObj = {
            message: err.response?.data?.message || 'Sesión expirada. Inicia sesión de nuevo.',
            status: statusCode,
          };
          if (!silent) {
            setError(errorObj);
            setLoading(false);
          }
          throw errorObj;
        }
      }

      const errorObj = {
        message: err.response?.data?.message || err.message || 'Error en el servidor',
        status: statusCode ?? null,
      };
      if (!silent) {
        setError(errorObj);
        setLoading(false);
      }
      throw errorObj;
    }
  }, []);

  return { loading, data, error, status, execute };
}

export default useFetch;
